import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Search, ShoppingBag, Lock, ArrowRight, Wrench } from "lucide-react";
import { api } from "@/lib/apiClient";
import { catalogFor } from "@/lib/productCatalog";
import { useTrattativa } from "@/context/TrattativaContext";

const MENU_BTN = "flex items-center gap-1.5 border-b-[3px] px-3 py-3 text-[13px] font-semibold uppercase tracking-wide text-forest transition-colors";

export function ProductLink({ p, onClick, className = "" }) {
  const inner = <><p.icon className="h-4 w-4 shrink-0" /><span className="flex-1">{p.label}</span>
    {p.soon && <span className="text-[10px] font-semibold uppercase text-muted-foreground">In arrivo</span>}</>;
  const cls = `flex items-center gap-3 border-b border-border/60 py-2.5 text-sm ${p.soon ? "pointer-events-none text-muted-foreground" : "text-forest hover:text-terracotta"} ${className}`;
  if (p.external) return <a href={p.external} target="_blank" rel="noreferrer" className={cls} onClick={onClick} data-testid={`mega-item-${p.code}`}>{inner}</a>;
  return <Link to={p.soon ? "#" : p.to} className={cls} onClick={onClick} data-testid={`mega-item-${p.code}`}>{inner}</Link>;
}

function MegaMenu({ line, onClose }) {
  return (
    <div className="absolute left-0 right-0 top-full z-40 border-t-4 bg-white shadow-2xl animate-in fade-in slide-in-from-top-1" style={{ borderColor: line.color }} data-testid={`mega-${line.key}`}>
      <div className="mx-auto grid max-w-6xl gap-8 px-8 py-7 md:grid-cols-[1fr_260px]">
        <div>
          <div className="mb-3 text-xs font-bold uppercase tracking-wider" style={{ color: line.color }}>{line.group}</div>
          {line.items.length === 0 ? <p className="flex items-center gap-2 text-sm text-muted-foreground"><Lock className="h-4 w-4" /> Nessun prodotto attivo per il tuo profilo.</p> : (
            <div className="grid gap-x-8 sm:grid-cols-2">{line.items.map((p) => <ProductLink key={p.code} p={p} onClick={onClose} />)}</div>
          )}
        </div>
        <Link to="/guida" onClick={onClose} className="rounded-xl bg-forest p-5 text-white transition-transform hover:-translate-y-0.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-white/60">In evidenza</div>
          <div className="mt-2 font-heading text-lg font-semibold">Le ultime novità del Portale</div>
          <p className="mt-1 text-xs text-white/75">Cosa è cambiato e come usarlo.</p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#5FD4CB]">Scopri <ArrowRight className="h-4 w-4" /></div>
        </Link>
      </div>
    </div>
  );
}

function ToolsMenu({ tools, onClose }) {
  return (
    <div className="absolute left-0 right-0 top-full z-40 border-t-4 border-forest bg-white shadow-2xl animate-in fade-in" data-testid="mega-tools">
      <div className="mx-auto grid max-w-6xl gap-x-8 px-8 py-6 sm:grid-cols-3">
        {tools.map((t) => (
          <Link key={t.key} to={t.to} onClick={onClose} data-testid={`mega-tool-${t.key}`} className="flex items-center gap-3 border-b border-border/60 py-2.5 text-sm text-forest hover:text-terracotta">
            <t.icon className="h-4 w-4" /><span className="flex-1">{t.label}</span>
            {t.badge > 0 && <span className="rounded-full bg-terracotta px-1.5 text-[10px] font-semibold text-white">{t.badge}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function TopNav({ user, tools }) {
  const [menu, setMenu] = useState(null);
  const lines = catalogFor(user);
  const close = () => setMenu(null);
  return (
    <div className="relative hidden border-t border-border/60 md:block" onMouseLeave={close}>
      <nav className="mx-auto flex max-w-6xl items-center px-5">
        {lines.map((l) => (
          <button key={l.key} onMouseEnter={() => setMenu(l.key)} onClick={() => setMenu(menu === l.key ? null : l.key)} data-testid={`topnav-${l.key}`}
            className={MENU_BTN} style={{ borderColor: menu === l.key ? l.color : "transparent" }}>
            <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />{l.short}<ChevronDown className="h-3.5 w-3.5" />
          </button>
        ))}
        <NavLink to="/pratica/nuova" onMouseEnter={close} data-testid="topnav-nuova-trattativa" className={({ isActive }) => `${MENU_BTN} ${isActive ? "border-terracotta" : "border-transparent"}`}>Nuova trattativa</NavLink>
        {tools.length > 0 && (
          <button onMouseEnter={() => setMenu("tools")} onClick={() => setMenu(menu === "tools" ? null : "tools")} data-testid="topnav-tools"
            className={`${MENU_BTN} ${menu === "tools" ? "border-forest" : "border-transparent"}`}><Wrench className="h-3.5 w-3.5" /> Strumenti<ChevronDown className="h-3.5 w-3.5" /></button>
        )}
      </nav>
      {menu === "tools" ? <ToolsMenu tools={tools} onClose={close} /> : menu && <MegaMenu line={lines.find((l) => l.key === menu)} onClose={close} />}
    </div>
  );
}

export function GlobalSearch({ user, mobile = false }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const t = useTrattativa();
  const navigate = useNavigate();
  const products = catalogFor(user).flatMap((l) => l.items).filter((p) => !p.soon && q.length >= 2 && p.label.toLowerCase().includes(q.toLowerCase()));
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const h = setTimeout(() => api.get("/anagrafiche/search", { params: { q } }).then(({ data }) => setRes((data || []).slice(0, 6))).catch(() => setRes([])), 250);
    return () => clearTimeout(h);
  }, [q]);
  useEffect(() => {
    const out = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", out); return () => document.removeEventListener("mousedown", out);
  }, []);
  const done = () => { setOpen(false); setQ(""); };
  return (
    <div ref={ref} className={mobile ? "relative md:hidden" : "relative hidden flex-1 md:block"}>
      <div className="flex items-center gap-2 rounded-full bg-sage px-4 py-2.5">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Cerca cliente o prodotto…"
          className="w-full bg-transparent text-sm text-forest outline-none placeholder:text-muted-foreground" data-testid={mobile ? "global-search-mobile" : "global-search"} />
      </div>
      {open && (res.length > 0 || products.length > 0) && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border bg-white p-2 shadow-xl" data-testid={mobile ? "global-search-results-mobile" : "global-search-results"}>
          {res.length > 0 && <div className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Clienti — avvia trattativa</div>}
          {res.map((a) => (
            <button key={a.id} onClick={() => { t.startWith({ id: a.id, nome: a.ragione_sociale }); done(); }} data-testid={`search-client-${a.id}`}
              className="flex w-full justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-sage"><span className="font-medium text-forest">{a.ragione_sociale}</span><span className="text-xs text-muted-foreground">{a.partita_iva || a.codice_fiscale}</span></button>
          ))}
          {products.length > 0 && <div className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Prodotti</div>}
          {products.map((p) => <button key={p.code} data-testid={`search-product-${p.code}`} onClick={() => { done(); p.external ? window.open(p.external, "_blank") : navigate(p.to); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-forest hover:bg-sage"><p.icon className="h-4 w-4" />{p.label}</button>)}
        </div>
      )}
    </div>
  );
}

export function CartButton() {
  const t = useTrattativa();
  return (
    <button onClick={t.openDrawer} className="relative rounded-full p-2 hover:bg-sage" data-testid="trattativa-cart" title="Trattativa in corso">
      <ShoppingBag className="h-6 w-6 text-forest" />
      {t.items.length > 0 && <span data-testid="trattativa-cart-count" className="absolute -right-0.5 -top-0.5 grid h-5 w-5 place-items-center rounded-full bg-terracotta text-[10px] font-bold text-white">{t.items.length}</span>}
    </button>
  );
}
